import useReactHookForm from "hooks/useReactHookForm";
import { useAppDispatch } from "hooks/redux";
import { Grid, Paper } from "@mui/material";
import DynamicForm from "shared/organisms/dynamicform";
import ContentRight from "shared/molecules/content-right";
import { TypographyHeading } from "shared/molecules/typography-layout";
import { useMutation } from "@apollo/client";
import { isEmpty } from "utils/helpers";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useHistory } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Nl3PackageScheduleInitialValues,
  Nl3PackageScheduleMandatoryFields,
  Nl3PackageScheduleProperties,
  Nl3PackageScheduleValidationSchema
} from "./helper";
import liveClassClientService from "utils/graphql/liveclassClient";
import { CREATE_PACKAGE_SCHEDULE } from "utils/graphql/services/live-classes/mutations";
import { isFieldsValidate } from "shared/organisms/dynamicform/helper";
import { useGoogleWorkspaceMapping } from "./workspace-mapping";

export const CreateNl3PackageSchedule = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { control, errors, getValues, watch, setValue } = useReactHookForm(
    Nl3PackageScheduleInitialValues,
    Nl3PackageScheduleValidationSchema,
    Nl3PackageScheduleMandatoryFields
  );

  const [CreatePackageSchedule, { loading, error, data }] = useMutation(
    CREATE_PACKAGE_SCHEDULE,
    {
      client: liveClassClientService,
    }
  );

  const {
    handleGoogleWorkspaceMappingError,
    workspaceUsersCreated,
    dialog: workspaceMappingDialog,
  } = useGoogleWorkspaceMapping(loading);

  const createPackageSchedule = async () => {
    try {
      const values = watch();
      const response: any = await CreatePackageSchedule({
        variables: {
          input: {
            projectIds: values?.projectIds,
            vendorId: values?.vendorId,
            vendorAccountId: Number(values?.vendorAccountId),
            scheduledStartTime: values?.start_date_time,
            scheduledEndTime: values?.end_date_time,
            liveclassPackageIds: values?.nlearn3LiveclassPackageIds,
            meetingTitle: values?.meetingTitle,
            subjectName: values?.subjectName,
            meetingSubTitle: values?.meetingSubTitle,
            thumbnailUrl: values?.thumbnailUrl,
            notesUrl: values?.notesUrl,
            employeeDepartmentId: values?.departmentId,
            employeeDesignationId: values?.designationId,
            teacherId: values?.teacherId,
            vendorUserId: values?.vendorUserId,
            isAllowed: workspaceUsersCreated,
          },
        },
      });

      if (!isEmpty(response?.data?.createPackageSchedule?.id)) {
        dispatch(
          snackbarRequest({
            message: `Created Package Schedule successfully`,
          })
        );
        history.replace(`/live-classes/package-schedules`);
      } else {
        dispatch(
          snackbarRequest({
            message: `Failed to create Package Schedule`,
            type: "error",
          })
        );
      }
    } catch (err: any) {
      const graphQLError = err?.graphQLErrors?.[0];
      if (handleGoogleWorkspaceMappingError(err)) {
        return;
      }

      dispatch(snackbarRequest({
        message:
          graphQLError?.message ||
          err?.message ||
          "Failed to create Package Schedule",
        type: "error",
      }));
    }
  };

  const handleCreateNl3PackageSchedule = () => createPackageSchedule();

  watch();

  return (
    <div>
      <TypographyHeading>Create Package Schedule</TypographyHeading>
      <Paper elevation={2} sx={{ px: 2, pb: 2, pt: 2 }}>
        <Grid container spacing={2}>
          <DynamicForm
            fields={Nl3PackageScheduleProperties}
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
          />
          <ContentRight sx={{ mt: 2 }}>
            <LoadingButton
              variant="contained"
              loadingPosition="start"
              sx={{
                width: "200px",
                height: "40px",
                marginTop: "15px",
                marginRight: "10px",
                padding: "7px 23.5px 7px 23.5px",
                cursor: "pointer",
                textAlign: "center",
              }}
              onClick={handleCreateNl3PackageSchedule}
              loading={loading}
              disabled={!isFieldsValidate(errors, Nl3PackageScheduleMandatoryFields, getValues)}
            >
              Create
            </LoadingButton>
          </ContentRight>
        </Grid>
      </Paper>
      {workspaceMappingDialog}
    </div>
  );
};
